import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  useAdminBloodRequestsList,
  useUpdateBloodRequest,
  useDeleteBloodRequest,
} from "../../../hooks/useAdminBloodRequests";
import DataTable from "../../../components/admin/DataTable";
import Pagination from "../../../components/admin/Pagination";
import SearchFilter from "../../../components/admin/SearchFilter";
import ConfirmDialog from "../../../components/admin/ConfirmDialog";
import BloodRequestEditModal from "./BloodRequestEditModal";

const BLOOD_GROUPS = ["A POSITIVE(+)", "A NEGATIVE(-)", "B POSITIVE(+)", "B NEGATIVE(-)", "O POSITIVE(+)", "O NEGATIVE(-)", "AB POSITIVE(+)", "AB NEGATIVE(-)"];
const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "cancelled", label: "Cancelled" },
  { value: "closed", label: "Closed" },
];

const AdminBloodRequests = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ bloodGroup: "", status: "" });
  const [editRequest, setEditRequest] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const queryParams = {
    page,
    limit: 20,
    ...(search && { search }),
    ...(filters.bloodGroup && { bloodGroup: filters.bloodGroup }),
    ...(filters.status && { status: filters.status }),
  };

  const { data, isLoading, error } = useAdminBloodRequestsList(queryParams);
  const updateMutation = useUpdateBloodRequest();
  const deleteMutation = useDeleteBloodRequest();

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((name, val) => {
    setFilters((prev) => ({ ...prev, [name]: val }));
    setPage(1);
  }, []);

  const handleStatusChange = async (request, newStatus) => {
    try {
      await updateMutation.mutateAsync({
        id: request._id,
        data: { status: newStatus },
      });
      toast.success(`Request marked as ${newStatus}`);
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget._id);
      toast.success("Blood request deleted successfully");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.message || "Failed to delete request");
    }
  };

  const columns = [
    { key: "patient", label: "Patient" },
    { key: "group", label: "Blood Group" },
    { key: "district", label: "District" },
    { key: "date", label: "Date" },
    { key: "status", label: "Status" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="flex gap-1 flex-wrap">
          <button
            className="btn btn-xs btn-info text-white"
            onClick={() => setEditRequest(row)}
          >
            Edit
          </button>
          {row.status !== "fulfilled" && (
            <button
              className="btn btn-xs btn-success text-white"
              onClick={() => handleStatusChange(row, "fulfilled")}
            >
              Fulfill
            </button>
          )}
          {row.status !== "cancelled" && (
            <button
              className="btn btn-xs btn-warning text-white"
              onClick={() => handleStatusChange(row, "cancelled")}
            >
              Cancel
            </button>
          )}
          <button
            className="btn btn-xs btn-error text-white"
            onClick={() => setDeleteTarget(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load blood requests: {error.message}</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Blood Requests</h1>

      <SearchFilter
        searchValue={search}
        onSearchChange={handleSearchChange}
        filters={[
          {
            name: "bloodGroup",
            label: "All Blood Groups",
            value: filters.bloodGroup,
            options: BLOOD_GROUPS.map((g) => ({ value: g, label: g })),
          },
          {
            name: "status",
            label: "All Status",
            value: filters.status,
            options: STATUS_OPTIONS,
          },
        ]}
        onFilterChange={handleFilterChange}
      />

      <DataTable columns={columns} data={data?.data} loading={isLoading} emptyMessage="No blood requests found" />

      {data?.pagination && (
        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-gray-700">
            Showing {((data.pagination.page - 1) * data.pagination.limit) + 1}–
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{" "}
            {data.pagination.total}
          </span>
          <Pagination
            currentPage={data.pagination.page}
            totalPages={data.pagination.totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {editRequest && (
        <BloodRequestEditModal
          request={editRequest}
          onClose={() => setEditRequest(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Blood Request"
        message={`Are you sure you want to delete the blood request for ${deleteTarget?.patient} (${deleteTarget?.group})?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AdminBloodRequests;
